import { Anvil, ArrowLeft, Menu, X } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { usePendingApprovalCountQuery } from "@/lib/api/hooks";
import { cn } from "@/lib/utils";
import { CommandPalette, JumpToButton, useCommandPalette } from "./CommandPalette";
import { type ShellNavItem, settingsNav, shellNav, sidebarModeFor } from "./navigation";
import { getSettingsReturnTo, rememberAppLocation } from "./settingsReturn";
import { WorkspaceMenu } from "./WorkspaceMenu";

/** Pending approvals, stamped beside the nav label; hidden at zero. */
function ApprovalsBadge() {
	const countQuery = usePendingApprovalCountQuery();
	const count = countQuery.data ?? 0;
	if (count === 0) return null;
	return (
		<span className="ml-auto inline-flex items-center rounded-sm border border-primary/30 bg-primary/10 px-1.5 py-px font-mono text-[10px] font-medium tracking-[0.1em] text-primary">
			{count}
		</span>
	);
}

const navItemClass =
	"group relative flex items-center gap-2 rounded-sm px-2.5 py-1.5 text-[13px] text-sidebar-foreground/70 transition-colors duration-150 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";

function NavItem({ item, onNavigate }: { item: ShellNavItem; onNavigate?: () => void }) {
	return (
		<NavLink
			to={item.to}
			onClick={onNavigate}
			className={({ isActive }) =>
				cn(
					navItemClass,
					onNavigate && "min-h-11 py-3",
					isActive && "bg-sidebar-accent font-medium text-sidebar-accent-foreground",
				)
			}
		>
			{({ isActive }) => (
				<>
					<span
						className={cn(
							"absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-none bg-primary transition-opacity duration-150",
							isActive ? "opacity-100" : "opacity-0 group-hover:opacity-40",
						)}
					/>
					{item.label}
					{item.badge === "approvals" ? <ApprovalsBadge /> : null}
				</>
			)}
		</NavLink>
	);
}

/** Settings mode: the way back out, then the settings sections. */
function SettingsSidebarNav({ onNavigate }: { onNavigate?: () => void }) {
	return (
		<>
			<Link
				to={getSettingsReturnTo()}
				onClick={onNavigate}
				className={cn(navItemClass, onNavigate && "min-h-11 py-3")}
			>
				<ArrowLeft className="size-3.5 shrink-0" />
				Back to app
			</Link>
			{settingsNav.map((section) => (
				<div key={section.label} className="mt-5">
					<p className="label-micro px-2.5 pb-1.5 text-[10px] text-sidebar-muted">
						{section.label}
					</p>
					<ul className="space-y-px">
						{section.items.map((item) => (
							<li key={item.to}>
								<NavItem item={item} onNavigate={onNavigate} />
							</li>
						))}
					</ul>
				</div>
			))}
		</>
	);
}

function ShellSidebar({
	onNavigate,
	onOpenPalette,
}: {
	onNavigate?: () => void;
	onOpenPalette: () => void;
}) {
	const mode = sidebarModeFor(useLocation().pathname);
	return (
		<>
			<div className="flex items-center gap-2.5 border-b border-sidebar-border px-4 py-4">
				<div className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-primary shadow-[inset_0_1px_0_0_oklch(1_0_0/0.2),inset_0_-2px_0_0_oklch(0_0_0/0.25)]">
					<Anvil className="size-4.5 text-primary-foreground" strokeWidth={2.25} />
				</div>
				<div className="min-w-0 leading-none">
					<p className="font-display font-wide text-[15px] font-extrabold uppercase tracking-[0.06em] text-sidebar-accent-foreground">
						Forgedock
					</p>
					<p className="label-micro mt-1 text-[9px] text-sidebar-muted">Forge Console</p>
				</div>
			</div>

			{/* Keyed by mode so the swap between the app nav and the settings
			    nav replays the short fade-in. */}
			<nav
				key={mode}
				aria-label={mode === "settings" ? "Settings" : "Main"}
				className="animate-nav-swap flex-1 overflow-y-auto px-3 py-5"
			>
				{mode === "settings" ? (
					<SettingsSidebarNav onNavigate={onNavigate} />
				) : (
					<ul className="space-y-px">
						{shellNav.map((item) => (
							<li key={item.to}>
								<NavItem item={item} onNavigate={onNavigate} />
							</li>
						))}
					</ul>
				)}
			</nav>

			{/* The foot stacks the jump-to box over the workspace switcher. Settings
			    has neither: the shortcut still opens the palette from there. */}
			{mode === "app" ? (
				<div className="flex flex-col gap-2 border-t border-sidebar-border p-3">
					<JumpToButton
						onOpen={() => {
							onNavigate?.();
							onOpenPalette();
						}}
						className={onNavigate && "min-h-11"}
					/>
					<WorkspaceMenu />
				</div>
			) : null}
		</>
	);
}

export function AppShell() {
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const palette = useCommandPalette();
	const openPalette = () => palette.onOpenChange(true);
	const { pathname, search, hash } = useLocation();

	useEffect(() => {
		rememberAppLocation(`${pathname}${search}${hash}`);
	}, [pathname, search, hash]);

	useEffect(() => {
		if (!mobileNavOpen) return;
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setMobileNavOpen(false);
		}
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [mobileNavOpen]);

	return (
		<div
			data-app-shell=""
			className="grid h-screen grid-cols-1 overflow-hidden bg-background md:grid-cols-[248px_1fr]"
		>
			{/* ─── Iron frame ─────────────────────────────────────────── */}
			<aside className="hidden h-screen flex-col border-r border-sidebar-border bg-sidebar md:flex">
				<ShellSidebar onOpenPalette={openPalette} />
			</aside>

			{mobileNavOpen ? (
				<div className="fixed inset-0 z-50 md:hidden">
					<button
						type="button"
						className="absolute inset-0 bg-black/60"
						aria-label="Close navigation"
						onClick={() => setMobileNavOpen(false)}
					/>
					<aside
						id="mobile-navigation"
						className="relative flex h-full w-[min(320px,86vw)] flex-col border-r border-sidebar-border bg-sidebar shadow-2xl"
					>
						<button
							type="button"
							className="absolute right-2.5 top-2.5 z-10 inline-flex size-11 items-center justify-center rounded-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
							aria-label="Close navigation"
							onClick={() => setMobileNavOpen(false)}
						>
							<X className="size-5" />
						</button>
						<ShellSidebar onNavigate={() => setMobileNavOpen(false)} onOpenPalette={openPalette} />
					</aside>
				</div>
			) : null}

			{/* ─── Workbench ──────────────────────────────────────────── */}
			<div className="flex h-screen min-w-0 flex-col">
				<header className="flex h-14 shrink-0 items-center justify-between border-b border-sidebar-border bg-sidebar px-3 md:hidden">
					<div className="flex items-center gap-2.5">
						<div className="flex size-8 items-center justify-center rounded-sm bg-primary">
							<Anvil className="size-4.5 text-primary-foreground" strokeWidth={2.25} />
						</div>
						<span className="font-display font-wide text-sm font-extrabold uppercase tracking-[0.06em] text-sidebar-accent-foreground">
							Forgedock
						</span>
					</div>
					<button
						type="button"
						className="inline-flex size-11 items-center justify-center rounded-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
						aria-label="Open navigation"
						aria-expanded={mobileNavOpen}
						aria-controls="mobile-navigation"
						onClick={() => setMobileNavOpen(true)}
					>
						<Menu className="size-5" />
					</button>
				</header>
				{/* A page that owns its own surface edge-to-edge (the transcript,
				    which carries a sticky header and its own floor) opts out of the
				    workbench gutter by marking itself `data-shell-bleed`. Declared
				    here rather than plumbed through the router so the page stays the
				    only thing that has to know. */}
				<main className="flex-1 overflow-y-auto">
					<div className="mx-auto max-w-[1200px] p-4 has-[[data-shell-bleed]]:flex has-[[data-shell-bleed]]:min-h-full has-[[data-shell-bleed]]:max-w-none has-[[data-shell-bleed]]:flex-col has-[[data-shell-bleed]]:p-0 sm:p-6 lg:p-8">
						<Suspense fallback={null}>
							<Outlet />
						</Suspense>
					</div>
				</main>
			</div>

			<CommandPalette {...palette} />
		</div>
	);
}
